import React, { useEffect, useMemo, useState } from 'react';
import AppSidebar from './AppSidebar';
import { Navbar } from './common/Catalyst/navbar';
import { SidebarLayout } from './common/Catalyst/sidebar-layout';
import { Heading, Subheading } from './common/Catalyst/heading';
import { Divider } from './common/Catalyst/divider';
import { Text } from './common/Catalyst/text';
import { FieldPicker } from './common/Custom/FieldPicker';
import { ErrorAlert } from './common/Custom/ErrorAlert';
import BankConnectionList from './BankConnectionList';
import BankAccountList from './BankAccountList';
import RemittanceSection from './RemittanceSection';
import ChargebackSection from './ChargebackSection';
import NsfSection from './NsfSection';
import PayablesSection from './PayablesSection';
import { CompanyReturn } from './clients/rust-api-client';
import { companyService } from './clients/ApiClients';
import { apiErrorMessage } from './utils/apiErrors';
import {
  getCompanyIdFromToken,
  getUserTypeFromToken,
} from './utils/TokenUtils';
import {
  UserType,
  canManageBankConnections,
  canSettlePayables,
  canViewBankAccounts,
} from './constants/UserTypes';

interface BankingPageProps {
  currentPage: string;
}

const BankingPage: React.FC<BankingPageProps> = ({ currentPage }) => {
  const userType = useMemo(() => getUserTypeFromToken(), []);
  const ownCompanyId = useMemo(() => getCompanyIdFromToken(), []);
  const showConnections = canManageBankConnections(userType);
  const showAccounts = canViewBankAccounts(userType);
  const isSuperAdmin = userType === UserType.SUPER_ADMIN;

  // A SuperAdmin may look at any company's connections and accounts (#400),
  // for support. Only look: the backend still lets a SuperAdmin write to
  // any company's bank, and reviewing that out is a go-live requirement
  // (knowledge/notes/open-question-super-admin-bank-access.md), so another
  // company's lists render read-only and the money-moving sections stay on
  // the SuperAdmin's own company.
  const [companyId, setCompanyId] = useState(ownCompanyId);
  const [companies, setCompanies] = useState<CompanyReturn[]>([]);
  const [companiesError, setCompaniesError] = useState<string | null>(null);
  const viewingOther = companyId !== ownCompanyId;
  const viewed = companies.find((c) => c.id === companyId);

  useEffect(() => {
    if (!isSuperAdmin) return;
    let live = true;
    companyService
      .getAllCompanies()
      .then(
        (res) =>
          live &&
          setCompanies(
            [...res.data].sort((a, b) => a.name.localeCompare(b.name))
          )
      )
      .catch(
        (err) =>
          live &&
          setCompaniesError(apiErrorMessage(err, "Couldn't load companies."))
      );
    return () => {
      live = false;
    };
  }, [isSuperAdmin]);

  // Owner/Admin only, and not super admins: settling moves money with
  // the person's own RSA code, and the backend refuses anyone else.
  const showPayables = canSettlePayables(userType);

  return (
    <SidebarLayout
      sidebar={<AppSidebar currentPage={currentPage} />}
      navbar={<Navbar>{/* Your navbar content */}</Navbar>}
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <Heading>Banking</Heading>
          <Subheading>
            {viewingOther && viewed
              ? `${viewed.name}'s bank connections and accounts`
              : "Manage your company's bank connections and accounts"}
          </Subheading>
        </div>
        {isSuperAdmin && (
          <FieldPicker
            label="Company"
            className="w-full sm:w-72"
            value={companyId}
            onChange={setCompanyId}
            placeholder="Choose a company"
            options={companies.map((c) => ({
              value: c.id,
              label: c.id === ownCompanyId ? `${c.name} (yours)` : c.name,
            }))}
          />
        )}
      </div>

      {companiesError && (
        <ErrorAlert className="mt-4" onDismiss={() => setCompaniesError(null)}>
          {companiesError}
        </ErrorAlert>
      )}

      {viewingOther && (
        <Text className="mt-4">
          Read-only. The company's own Owner manages its connections.
          Remittance, chargebacks and NSF items are shown for your own company
          only.
        </Text>
      )}

      <Divider className="mt-4 mb-6"></Divider>

      {/* Keyed by company, so switching starts each list fresh: no rows or
          open dialog carried over from the company before. */}
      {showConnections && (
        <BankConnectionList
          key={`connections-${companyId}`}
          companyId={companyId}
          readOnly={viewingOther}
        />
      )}

      {showConnections && showAccounts && <Divider className="my-10" />}

      {showAccounts && (
        <BankAccountList key={`accounts-${companyId}`} companyId={companyId} />
      )}

      {/* Owner/Admin only, matching the endpoint's own authorization:
          a connection belongs to a company, and so does the decision
          about what is read from it. */}
      {showConnections && !viewingOther && <Divider className="my-10" />}
      {showConnections && !viewingOther && <RemittanceSection />}
      {/* After the lockbox, because money comes in before it goes
          back out and a manager reads the page in that order. */}
      {showConnections && !viewingOther && <ChargebackSection />}
      {/* Last, and the one with a clock on it: items the bank will
          decide for us if nobody does. */}
      {showConnections && !viewingOther && <NsfSection />}
      {/* Money going out by ACH, and coming in: the day's batch a person
          settles with their RSA code. Never for another company: a
          super admin cannot settle at all, and the queue is the company's. */}
      {showPayables && !showConnections && <Divider className="my-10" />}
      {showPayables && !viewingOther && <PayablesSection />}
    </SidebarLayout>
  );
};

export default BankingPage;
